import React, { FC, createRef, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import {
  ControllerRenderProps,
  ControllerFieldState,
  UseControllerProps,
} from 'react-hook-form'
import styled, { css } from 'styled-components'

import {
  Flex,
  Text,
  TextInput as TextInputDS,
  ITextInput as ITextInputDS,
  IconButton,
} from '@wirtualna-polska/n2cr-ds'
import { InfoIconTooltip } from '@ads/front-core/atoms'
import { UseAdsFormReturn } from '@ads/front-core/hooks/useAdsForm'
import { useCurrency } from '@ads/front-core/hooks/useCurrency'
import {
  formatNumber,
  dateToLocateDate,
  copyToClipboard,
} from '@ads/front-core/utils'
import { lng } from '@ads/front-core/locales'
import InputColor from './InputColor'
import { formatInputValue } from './utils'
import { Tooltip } from '@ads/front-core/atoms'

export interface ITextInput {
  field?: ControllerRenderProps
  fieldState?: ControllerFieldState
  defaultValue?: UseControllerProps['defaultValue']
  value?: UseControllerProps['defaultValue']
  type?: ITextInputDS['type'] | 'currency'
  placeholder?: string
  disabled?: boolean
  prefix?: string
  sufix?: string
  hasError?: boolean
  step?: number
  readOnly?: boolean
  tooltip?: string
  labelAfterInput?: string
  withClipboardCopy?: boolean
  hideNumberControls?: boolean
  onBlur?: (e?: React.FormEvent<HTMLInputElement>) => void
  onChange?: (e?: React.FormEvent<HTMLInputElement>) => void
  trigger?: UseAdsFormReturn<unknown>['trigger']
}

const TextInput: FC<ITextInput> = ({
  field,
  fieldState,
  type,
  placeholder,
  disabled,
  prefix,
  sufix,
  hasError,
  step = 0.01,
  readOnly,
  defaultValue,
  value,
  tooltip,
  labelAfterInput,
  withClipboardCopy,
  hideNumberControls,
  onBlur,
  onChange,
  trigger,
}) => {
  const dispatch = useDispatch()

  const ref = createRef<HTMLInputElement>()
  const asColorFiler = type === 'color'

  const { calculateCurrency, currency, isDefaultCurrency } = useCurrency()

  const handleOnKeyDown = (e?: React.KeyboardEvent<HTMLInputElement>): void => {
    if ((type !== 'number' && type !== 'currency') || !e) {
      return
    }
    if (e.key.length !== 1 || e.metaKey || e.ctrlKey) {
      return
    }
    if (!e.key.match(/^[\d,.]{1}$/)) {
      e.preventDefault()
    }
    if (step === 1 && !e.key.match(/^\d{1}$/)) {
      e.preventDefault()
    }
  }

  const onInputBlur = (e?: React.FormEvent<HTMLInputElement>) => {
    try {
      e.currentTarget.value = formatInputValue(
        e.currentTarget.value,
        type,
        step,
      )
    } catch (err) {
      console.error(err)
    }
    if (
      typeof field?.onChange === 'function' &&
      typeof e.currentTarget.value === 'string'
    ) {
      e.currentTarget.value = e.currentTarget.value.trim()
      field.onChange(e)
    }
    if (typeof field?.onBlur === 'function') {
      field.onBlur()
      if (typeof trigger === 'function') {
        trigger(field?.name as any)
      }
    }
    if (typeof onBlur === 'function') {
      onBlur(e)
    }
  }

  const onInputChange = (e?: React.FormEvent<HTMLInputElement>) => {
    if (typeof field?.onChange === 'function') {
      field.onChange(e)
      if (fieldState?.isTouched && typeof trigger === 'function') {
        trigger(field?.name as any)
      }
    }
    if (typeof onChange === 'function') {
      onChange(e)
    }
  }

  const handleOnCopyToClipboard = () => copyToClipboard(field.value, dispatch)

  useEffect(() => {
    field?.ref(ref.current)
    if (['number', 'currency'].includes(type) && ref) {
      ref.current.setAttribute('min', '0')
      if (step) {
        ref.current.setAttribute('step', String(step))
      }
    }
  }, [field, ref, step])

  useEffect(() => {
    if (ref?.current?.value) {
      ref.current.value = formatInputValue(ref.current.value, type, step)
    }
  }, [ref?.current?.nodeType])

  return (
    <TextInputWrapper>
      {asColorFiler && (
        <InputColor
          value={field?.value || '#000000'}
          onChange={field?.onChange}
        />
      )}
      <InputWrapper hasError={hasError} disabled={disabled}>
        <TextInputStyled
          data-testid={`input-${field?.name}`}
          onBlur={onInputBlur}
          onChange={onInputChange}
          value={
            field?.value
              ? field?.value
              : typeof field?.onChange === 'function'
              ? ''
              : value
          }
          ref={ref}
          disabled={disabled}
          placeholder={placeholder}
          type={asColorFiler ? 'text' : type === 'currency' ? 'number' : type}
          prefix={prefix}
          readOnly={readOnly}
          withTooltip={!!tooltip}
          withSufix={!!sufix}
          error={hasError}
          defaultValue={defaultValue}
          onKeyDown={handleOnKeyDown}
          prefixIconColor={asColorFiler && field?.value}
          hideNumberControls={hideNumberControls}
        />
        {withClipboardCopy && type !== 'currency' && (
          <ClipboardButton name="20-copy" onClick={handleOnCopyToClipboard} />
        )}
        {!!sufix && (
          <Sufix
            hasError={hasError}
            disabled={disabled}
            isFieldEmpty={!field?.value}
          >
            {sufix}
          </Sufix>
        )}
        {type === 'currency' && !isDefaultCurrency && (
          <CurrencyWrapper>
            <Tooltip
              placement="top"
              text={lng('common:currencyTooltip', {
                rate: formatNumber(currency.rate),
                symbol: currency.symbol,
                date: dateToLocateDate(currency.date),
              })}
            >
              <CurrencyValue disabled={disabled}>
                {field?.value > 0
                  ? formatNumber(calculateCurrency(field.value))
                  : '-'}{' '}
                {currency.symbol}
              </CurrencyValue>
            </Tooltip>
          </CurrencyWrapper>
        )}
      </InputWrapper>
      {!!tooltip && <InfoIconTooltipStyled tooltipMessage={tooltip} />}
      {!!labelAfterInput &&
        (type !== 'currency' || (type === 'currency' && isDefaultCurrency)) && (
          <Text>{labelAfterInput}</Text>
        )}
    </TextInputWrapper>
  )
}

const InfoIconTooltipStyled = styled(InfoIconTooltip)`
  ${p => css`
    position: absolute;
    top: ${p.theme.space.xxs + 2}px;
    right: ${p.theme.space.xs}px;
  `}
`

const InputWrapper = styled(Flex)<{ hasError?: boolean; disabled?: boolean }>`
  ${p => css`
    flex: 1;

    ${Sufix}, input {
      border-color: ${p.hasError
        ? p.theme.colors.alert20
        : p.theme.colors.grey20};
    }

    &:hover {
      ${Sufix}, input, input:focus {
        border-color: ${p.hasError
          ? p.theme.colors.alert20
          : p.disabled
          ? p.theme.colors.grey20
          : p.theme.colors.primary20};
      }
    }
  `}
`

const CurrencyWrapper = styled(Flex)`
  ${p => css`
    &:after {
      content: '';
      position: absolute;
      background-color: ${p.theme.colors.grey05};
      top: -1px;
      bottom: -1px;
      left: -${p.theme.space.xxxs}px;
      width: ${p.theme.space.xxxs}px;
    }

    position: relative;
    border: ${p.theme.borders.thinGrey20};
    border-left: 0;
    border-radius: 0 ${p.theme.radii.rounded} ${p.theme.radii.rounded} 0;
    background: ${p.theme.colors.grey05};
    padding: 7px 15px;
    align-items: center;
  `}
`

const CurrencyValue = styled(Text)<{ disabled?: boolean }>`
  ${p => css`
    display: block;
    font-size: ${p.theme.fontSizes.s}px;
    color: ${p.disabled ? p.theme.colors.greyDisabled : p.theme.colors.grey50};
    min-width: 86px;
    max-width: 110px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    text-align: right;
  `}
`

const Sufix = styled(Text)<{
  hasError?: boolean
  isFieldEmpty?: boolean
  disabled?: boolean
}>`
  ${p => css`
    z-index: 1;
    display: flex;
    align-items: center;
    padding: 7px 15px 7px 0;
    border: ${p.theme.borders.thinGrey20};
    border-left: 0;
    border-radius: 0 ${p.theme.radii.rounded} ${p.theme.radii.rounded} 0;
    background-color: ${p.disabled ? p.theme.colors.grey05 : '#fff'};
    color: ${p.disabled
      ? p.theme.colors.greyDisabled
      : p.isFieldEmpty
      ? p.theme.colors.grey50
      : p.theme.colors.grey100};
  `}
`

const TextInputWrapper = styled(Flex)`
  ${p => css`
    position: relative;
    flex-direction: row no-wrap;
    align-items: center;
    gap: ${p.theme.space.xxs}px;
  `}
`

const TextInputStyled = styled(TextInputDS)<
  ITextInputDS & {
    prefixIconColor?: string
    readOnly?: boolean
    withTooltip?: boolean
    withSufix?: boolean
    hideNumberControls?: boolean
  }
>`
  ${p => css`
    flex: 1;
    width: auto;
    z-index: 1;

    input:focus {
      outline: none;
      box-shadow: none;
      border-color: ${p.error ? p.theme.colors.alert20 : p.theme.colors.grey20};
    }

    ${p.withSufix &&
    css`
      input {
        border-right: 0;
        border-radius: ${p.theme.radii.rounded} 0 0 ${p.theme.radii.rounded};
      }
    `}

    ${(p.hideNumberControls || p.withSufix) &&
    css`
      input::-webkit-outer-spin-button,
      input::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }

      input[type='number'] {
        -moz-appearance: textfield;
      }
    `}

    ${p.withTooltip &&
    css`
      input {
        padding-right: ${p.theme.space.l}px;
      }
    `}

    ${p.readOnly &&
    css`
      input {
        cursor: not-allowed;
      }
    `}

    svg {
      color: ${p.prefixIconColor || '#000'};
    }
  `}
`

const ClipboardButton = styled(IconButton)`
  ${p => css`
    position: absolute;
    bottom: -1px;
    right: ${p.theme.space.xxxs}px;

    &:active {
      opacity: 1;
    }

    &:focus {
      box-shadow: none;
    }
  `}
`

export default TextInput
